// Libraries
import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// Components
import RankedTooltipAttribute from 'admin/engagement/reporting/components/RankedTooltipAttribute';
import CustomTooltipAttribute from 'admin/engagement/reporting/components/CustomTooltipAttribute';

const handleNestedPayloadKey = (payloadKey, entryPayload) =>
  payloadKey.reduce((object, accessor) => {
    if (object[accessor]) {
      return object[accessor];
    }
    return object;
  }, entryPayload);

const generateAttributes = (attributes, entryPayload, ranked) =>
  attributes.map(({ tooltipLabel, payloadKey, percentage }) => {
    const tooltipValue = Array.isArray(payloadKey)
      ? handleNestedPayloadKey(payloadKey, entryPayload)
      : entryPayload[payloadKey];

    return ranked ? (
      <RankedTooltipAttribute
        key={`${tooltipLabel}-${percentage}`}
        tooltipLabel={tooltipLabel}
        tooltipValue={tooltipValue instanceof Object ? '0' : String(tooltipValue)}
        percentage={percentage}
      />
    ) : (
      <CustomTooltipAttribute
        key={`${tooltipLabel}-${percentage}`}
        tooltipLabel={tooltipLabel}
        tooltipValue={String(tooltipValue)}
        percentage={percentage}
      />
    );
  });

const CustomTooltip = ({ active, payload, label, ranked, attributes, children }) => {
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (tooltipRef && tooltipRef.current) {
      const elemClientRect = tooltipRef.current.getBoundingClientRect();
      const maxWidth = document.body.clientWidth - elemClientRect.x - 10;

      if (elemClientRect.width > maxWidth) {
        tooltipRef.current.style.width = `${maxWidth}px`;
      }
    }
  });

  if (active && payload && payload.length > 0) {
    const entryPayload = payload[0].payload;

    return (
      <div ref={tooltipRef} className="custom-tooltip">
        {attributes ? (
          <>
            <div className="custom-tooltip__label">
              <span>{label || entryPayload.label}</span>
            </div>
            {generateAttributes(attributes, entryPayload, ranked)}
          </>
        ) : (
          children(entryPayload)
        )}
      </div>
    );
  }

  return null;
};

CustomTooltip.defaultProps = {
  ranked: false,
  active: false,
  payload: [],
  label: '',
  attributes: null,
  children: null,
};

CustomTooltip.propTypes = {
  ranked: PropTypes.bool,
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
  label: PropTypes.string,
  attributes: PropTypes.arrayOf(
    PropTypes.shape({
      tooltipLabel: PropTypes.string.isRequired,
      percentage: PropTypes.bool,
    })
  ),
  children: PropTypes.func,
};

export default CustomTooltip;
